import React, {Fragment} from "react";
import makeStyles from "@mui/styles/makeStyles";
import {useTranslation} from "react-i18next";
import _ from "lodash";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import moment from "moment";
import {TimerCountUpSimple} from "../../controls/agyle/timer";
import {IconButton, Tooltip} from "@mui/material";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import CheckIcon from "@mui/icons-material/Check";
import {AgyleNamedIcon} from "../../components/icons/agyle";
import clsx from "clsx";
import config from "../../app/config";
import DictParser from "../../util/dictParser";
import {useDispatch, useSelector} from "react-redux";
import {
    ambulanceCodes,
    chiefComplaint,
    getEcdsVersion,
    referredToService,
    streamingTypeDict,
} from '../../store/reducers/dicts';
import Typography from "@mui/material/Typography";
import {Bear} from "../../components/icons/icons";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import {setDemographicPatient} from "../../store/reducers/patient";
import {useHistory} from "react-router-dom";
import {hasPermission, Permissions, sendUserAudit} from "../../store/reducers/user";
import {setSnackbar} from "../../store/reducers/globals";
import FindInPageIcon from '@mui/icons-material/FindInPage';
import {calculateArrivalDate, getAdmissionStartDate} from '../../util/patientUtil';
import {getSafeguardingLevel, selectSafeLevelColor} from '../../util/patientContextUtil';
import {findPatientFlagObject} from './mainScreen/util/patientFlagsUtil';
import {getReferralsColor} from '../../controls/agyle/tasks/taskUtil';
import {getInfectionIcon} from './forms/formsUtil';
import {useTheme} from '@mui/styles';
import { AUDIT_TYPES } from "../../util/auditUtil";

const useStyles = makeStyles((theme) => ({
    dialogRoot: {},
    main: {
        padding: "10px 0",
        minHeight: 40,
        display: "flex",
        borderBottom: "1px solid grey"
    },
    priority: {
        display: "inline-block",
        fontWeight: "bold",
        marginRight: 20,
    },
    complaint: {
        display: "inline-block",
    },
    presentingComplaintClass: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        paddingRight: 15,
        cursor: "pointer"
    },
    timer: {
        borderRadius: 10,
        display: "inline-block",
        overflow: "hidden",
        "& .timer-count-up": {
            fontSize: 14,
            height: 20,
            width: "max-content"
        },
    },
    colorSystem: {
        width: 16,
        height: 16,
        backgroundColor: 'white',
        display: "inline-block",
        marginRight: 10,
        verticalAlign: 'middle',
        border: "1px solid #222",
    },
    discriminator: {
        display: "inline-block",
        fontSize: "small",
        verticalAlign: 'middle'
    },
    topIcon: {
        display: "inline-block",
        "& svg": {
            color: "#DC4A5D",
        },
        "&.none": {
            "& svg": {
                color: "#01af69"
            }
        },
        "&.confirm": {
            "& svg": {
                color: "#DC4A5D",
            }
        },
        "&.suspected": {
            "& svg": {
                color: "#FEB03A"
            }
        },
    },
    referral: {
        display: "inline-block",
        width: 40,
        margin: 1,
        height: 30,
        textAlign: "center",
        lineHeight: "30px",
        color: "#fff",
        borderRadius: 10,
        fontSize: "small",
        "&.completed": {
            border: "2px solid #000",
            color: "#000",
            lineHeight: "25px"
        },
    },
    scoreColor: {
        display: "inline-block",
        width: '30px',
        marginLeft: '13px',
        textAlign: "center",
        padding: 5,
        borderRadius: '5px'
    },
    streamingType: {
        display: "inline-block",
        width: 40,
        marginLeft: 13,
        textAlign: "center",
        padding: 5,
        borderRadius: 5,
        color: "#202020",
        cursor: "pointer",
    },
    obsColorLow: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
    },
    obsColorMediumLow: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
    },
    obsColorMedium: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
    },
    obsColorHigh: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
    },
    obsColorUnknown: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
    },
    singleIconFlag: {
        display: "inline-block",
        backgroundColor: theme.palette.flags.singleIconFlag.default,
        textAlign: "center",
        width: 30,
        height: 30,
        margin: "0 3px",
        borderWidth: 1,
        borderRadius: "8px",
        border: "1px solid #414141",

        "& svg": {
            transform: `scale(${30 / 24})`,
        },
        "&.active": {
            backgroundColor: theme.palette.flags.singleIconFlag.active,
            color: theme.palette.flags.singleIconFlag.activeColor,
        }
    },
    singleCheckedFlag: {
        display: "inline-block",
        backgroundColor: theme.palette.flags.singleIconFlag.noneColor,
        textAlign: "center",
        width: 26,
        height: 26,
        margin: "0 3px",
        borderWidth: 1,
        borderRadius: "8px",
        border: "1px solid #414141",
        '& svg': {
            color: 'white',
        }
    },
    clinitianTimestamp: {
        display: 'flex',
        marginLeft: '45px',
        alignItems: 'center'
    },
    preLine: {
        whiteSpace: 'pre-line'
    },
    preventTooLong: {
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        paddingRight: 5,
    },
    flagsContainer: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        wordBreak: 'break-word'
    },
    showAllButton: {
        marginTop: "3px",
        paddingLeft: "8px",
        paddingRight: "8px",
        paddingTop: "2px",
        paddingBottom: "2px",
        borderRadius: "5px",
        maxWidth: "max-content",
        backgroundColor: "#448FFF",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        wordBreak: "keep-all",
    },
    planInnerItem: {
        marginLeft: '15px'
    },
    conclusionIcon: {
        transform: "translate(20px, -10px)",
        width: '30px',
        height: '30px'
    },
    smallIconText: {
        width: '30px',
        height: '30px',
        paddingTop: '4px'
    },
    patientStatus: {
        borderRadius: 10,
        padding: 5,
        width: '95%',
        "&.temporary": {
            color: "#ffffff",
            background: "#83AFB9",
        },
        "&.fastRegistry": {
            color: "#ffffff",
            background: "#A883B9",
        },
        "&.noDiagnosisWarning": {
            color: "#ffffff",
            background: "#ffb74d",
        },
    },
    avatarCustom: {
        width: "auto !important",
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 15,
        backgroundColor: theme.palette.background.paper,
        color: "#fff",
    },
    bedStatus: {
        borderRadius: 10,
        padding: 5,
        textAlign: "center",
        margin: "0 10px",
        "&.Ready": {
            backgroundColor: theme.palette.acuityColors.color4.background,
            color: theme.palette.acuityColors.color4.text,
        },
        "&.Requested": {
            backgroundColor: theme.palette.acuityColors.color1.background,
            color: theme.palette.acuityColors.color1.text,
        },
        "&.Allocated": {
            backgroundColor: theme.palette.acuityColors.color3.background,
            color: theme.palette.acuityColors.color3.text,
        },
        "&.Canceled": {
            backgroundColor: theme.palette.acuityColors.color5.background,
            color: theme.palette.acuityColors.color5.text,
        },
    },
    flagChip: {
        marginRight: 2,
        '& .MuiChip-label': {
            padding: "0 6px",
        }
    }
}));

const NursingNotesWithTooltip = ({note, fieldName}) => {
    const classes = useStyles();

    const getNoteDescription = (singleNote, index, isTooltip = false) => {
        if (_.isUndefined(index)) {
            return !fieldName ? singleNote?.text : singleNote?.value?.__description
        }
        if(!fieldName) return singleNote[index]?.text;
        if(!config.chipInPriorityList || isTooltip) return singleNote[index]?.value?.__description;

        let colors = config.diagnosisColorsSuspected;

        if(fieldName === "diagnosis") {
            if(singleNote[index]?.qualifier && singleNote[index]?.qualifier?.snomedDescription === "Suspected")
                colors = config.diagnosisColorsSuspected || {chip: "#ef902c", main: "#693a08"}
            else
                colors = config.diagnosisColorsConfirm || {chip: "#44bb8b", main: "#1b4b38"}
        }
        if(fieldName === "treatment" || fieldName === "investigations") {
            if( _.isEmpty(singleNote[index]?.procedureDate) || _.isEmpty(singleNote[index]?.procedureTime))
                colors = config.treatmentInvColors || {chip: "#44bb8b", main: "#1b4b38"}
            else
                colors = config.treatmentInvColorsRequested || {chip: "#ad3c3c", main: "#451818"}
        }

        return <Chip style={{color: "white", background: colors.chip, marginBottom: 3}}
                     avatar={
                         (config.snomedCodeOnPriorityListInDiagnosis && <div style={{
                             lineHeight: "24px",
                             color: "#fff",
                             background: colors.main,
                         }}
                              className={classes.avatarCustom}
                         >
                             {singleNote[index]?.value?.snomedCode ? singleNote[index]?.value?.snomedCode : "UNK"}
                         </div> )
                     }
                     label={singleNote[index]?.value?.__description}/>
    }

    const renderCheckIcon = (singleNote, index) => {
        if(_.isUndefined(index)) {
            return (!fieldName ? (
                singleNote?.checked && <div className={classes.singleCheckedFlag}>{<CheckIcon />}</div>
            ) : (
                singleNote?.qualifier?.__description === "Confirmed diagnosis" && <div className={classes.singleCheckedFlag}>{<CheckIcon />}</div>
                // <CheckIcon color={"success"} />
            ))
        }
        return (!fieldName ? (note[index]?.checked && <div className={classes.singleCheckedFlag}>{<CheckIcon />}</div>) :
            (singleNote[index]?.qualifier?.__description === "Confirmed diagnosis" && <div className={classes.singleCheckedFlag}>{<CheckIcon />}</div>))
                // <CheckIcon color={"success"} />))
    }

    if (note.length === 0) return "-"
    if (note.length === 1) {
      return (
        <Typography variant="body2" sx={{ pr: 1 }}>
          <Tooltip title={ getNoteDescription(note, 0, true) }>
            <div className={classes.preventTooLong}>
              <span className={classes.preventTooLong}>
                1. { getNoteDescription(note, 0) }
              </span>
                {(!config.chipInPriorityList || !fieldName) &&
                    <span className={classes.planInnerItem}>{renderCheckIcon(note, 0) }</span>
                }
            </div>
          </Tooltip>
        </Typography>
      );
    } else if (note.length == 2) {
      return (
        <>
          <Typography variant="body2" sx={{ pr: 1 }}>
            <Tooltip title={ getNoteDescription(note, 0, true) }>
              <div className={classes.preventTooLong}>
                <span className={classes.preventTooLong}>
                  1. { getNoteDescription(note, 0) }
                </span>
                  {(!config.chipInPriorityList || !fieldName) &&
                    <span className={classes.planInnerItem}>{renderCheckIcon(note, 0) }</span>
                  }
              </div>
            </Tooltip>
          </Typography>
          <Typography variant="body2" notesx={{ pr: 1 }}>
            <Tooltip title={ getNoteDescription(note, 1, true) }>
              <div className={classes.preventTooLong}>
                <span className={classes.preventTooLong}>
                  2. { getNoteDescription(note, 1) }
                </span>
                  {(!config.chipInPriorityList || !fieldName) &&
                    <span className={classes.planInnerItem}>{renderCheckIcon(note, 1) }</span>
                  }
              </div>
            </Tooltip>
          </Typography>
        </>
      );
    } else {
      return (
        <Typography variant="body2" sx={{ pr: 1 }}>
          <Tooltip
            title={
                <span className={classes.preventTooLong}>
                    { getNoteDescription(note, 0) }
                </span>
            }
          >
            <div className={classes.preventTooLong}>
              <span className={classes.preventTooLong}>
                1. { getNoteDescription(note, 0) }
              </span>
                {(!config.chipInPriorityList || !fieldName) &&
                    <span className={classes.planInnerItem}>{renderCheckIcon(note, 0) }</span>
                }

            </div>
          </Tooltip>
          <Tooltip
            title={note.map((oneNote, index) => (
              <div className={classes.flexContainer} key={"note" + note + index}>
                <span
                  className={classes.flexContainer}
                  style={{ fontSize: "1.3em" }}
                >
                  {index + 1}. { getNoteDescription(oneNote) }
                </span>
                  {(!config.chipInPriorityList || !fieldName) &&
                      <span
                          className={classes.planInnerItem}
                          style={{textAlign: "center"}}
                      >
                       {renderCheckIcon(oneNote)}
                    </span>
                  }
              </div>
            ))}
          >
            <div
             className={classes.showAllButton}
            >
              Show all
            </div>
          </Tooltip>
        </Typography>
      );
    }
}

function PatientInList(props) {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const theme = useTheme();

    const dictChiefComplaint = useSelector(chiefComplaint);
    const dictReferredToService = useSelector(referredToService);
    const ambulance = useSelector(ambulanceCodes);
    const version = dispatch(getEcdsVersion(props.patient?.admission?.startDate));
    const streamingType = useSelector(streamingTypeDict);

    const [open, setOpen] = React.useState(false);
    let presentingComplaint = "-";

    if (props.patient?.clerking?.historicPresentingComplaint) presentingComplaint = props.patient.clerking?.historicPresentingComplaint;
    else if (props.patient?.studies?.seeAndTreat?.historyOfPresentingComplaint) presentingComplaint = props.patient.studies?.seeAndTreat?.historyOfPresentingComplaint;
    else if (props.patient?.triage?.historicPresentingComplaint) presentingComplaint = props.patient.triage?.historicPresentingComplaint;
    else if (props.patient?.metadata?.historyOfPresentingComplaint) presentingComplaint = props.patient.metadata?.historyOfPresentingComplaint;

    const formatPatientName = (patient) => {
        let ptStatus = "";
        ptStatus = checkNoDiagnosis() ? "noDiagnosisWarning" : ptStatus
        if (config?.priorityList?.enableDemographicUpdate) {
            ptStatus = props.patient?.patient?.tags?.includes("fastRegistry") ? "fastRegistry" : ptStatus;
            ptStatus = props.patient?.patient?.tags?.includes("temporary") ? "temporary" : ptStatus;
        }

        return (
            <div className={clsx(ptStatus !== "" && classes.patientStatus, ptStatus)}>
                <strong>{_.toUpper(patient?.familyName ? patient?.familyName : "")}</strong>,{" "}
                {patient?.firstName ? patient?.firstName.substring(0, 1) : ""}.
                {(props.fromAdmit && ptStatus === "temporary" && dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT))) &&
                    <IconButton
                        onClick={() => props.mergeTemporary(props.patient)}
                        size="small"
                        style={{marginLeft: 'auto'}}
                        disableRipple
                    >
                        <FindInPageIcon fontSize="small"/>
                    </IconButton>
                }
            </div>
        );
    };

    const checkNoDiagnosis = () => {
        if (config?.priorityList?.noDiagnosisWarning && props.patient) {
            if (_.isEmpty(props.patient?.diagnosis?.primary) && (props.patient?.seniorReview?.when || props.patient?.firstClinician?.when)) {
                const date = getLatestDate();
                const diff = moment().diff(date, "minute");
                return diff > 60;
            }
        }
        return false;
    };

    const getLatestDate = () => {
        const seniorReview = props.patient?.seniorReview?.when ? moment(props.patient?.seniorReview?.when) : null;
        const firstClinician = props.patient?.firstClinician?.when ? moment(props.patient?.firstClinician?.when) : null;
        if (seniorReview && firstClinician) {
            return firstClinician.isAfter(seniorReview) ? firstClinician : seniorReview;
        } else {
            return firstClinician ? firstClinician : seniorReview;
        }
    };

    const getPatientAge = patient => {
        let ageText = "UNK";
        if (patient?.patient?.dob) {
            const dob = moment(patient.patient.dob);
            const age = moment.duration(moment().diff(dob)).asYears();
            ageText = Math.floor(age);
        }
        return ageText;
    };

    const formatDependency = (dependency) => {
        if (dependency) {
            return (
                <Fragment>
                    <div className={classes.colorSystem} style={{
                        backgroundColor: dependency?.color?.toLowerCase(),
                    }}>
                    </div>
                    <div className={classes.discriminator}>
                                <span>
                                {dependency?.__description}
                            </span>
                    </div>
                </Fragment>
            );
        }
        return "-";
    };

    const formatBedRequest = (csm) => {
        if (csm) {
            return csm?.bed === "req" ? "Requested" : csm?.bed === "ready" ? "Ready" : "-";
        }
        return "-";
    };

    const formatBedRequestUHD = (bedRequest) => {
        if (!bedRequest) return '-';
        let val = 'Requested';
        if (bedRequest?.canceled) val = 'Cancelled';
        else if (bedRequest?.ready) val = 'Ready';
        else if (bedRequest?.allocated) val = 'Allocated';

        return (
            <div className={clsx(classes.bedStatus, val)}>
                {val}
            </div>
        );
    };

    const renderCovid = () => {
        return getInfectionIcon(props.patient?.metadata, classes.topIcon, false, true, true)
    };

    const renderSepsis = () => {
        if (props.patient.metadata?.sepsisPathway?.status === 0) {
            return (
                <div className={classes.topIcon}>
                    <AgyleNamedIcon
                        name={props.patient.metadata.sepsisPathway.neutropenic ? "neutropenicSepsis" : "sepsis"}
                    />
                </div>
            );
        }
        return "-";
    };

    const renderSafeguardingLevel = () => {
        let level = getSafeguardingLevel(props.patient);
        if (level) {
            return (
                <div
                    style={{
                        color: selectSafeLevelColor(level),
                        zIndex: 1,
                        textAlign: "center"
                    }}
                    >
                    <Bear />
                </div>
            );
        }
        return "";
    };

    const renderChiefComplaint = () => {
        let chief = null;
        const description = props.patient?.admission?.initialAEComplaintDescription;
        const code = props.patient?.admission?.initialAEComplaintSnomedCode;

        _.forEach(dictChiefComplaint, el => {
            if (el.ecdsVersion === version && ((code && _.startsWith(el?.snomedCode, code)) || _.startsWith(el?.__description, description))) {
                chief = el;
            }
        });

        if (props.patient?.triage?.chiefComplaint) {
            const chiefComplaint = props.patient?.triage?.chiefComplaint ? props.patient?.triage?.chiefComplaint : chief;
            return <Chip
                color="primary"
                size="small"
                label={DictParser.chiefComplaint(chiefComplaint, false)}/>;
        } else {
            // Check from PAS
            if ((props.patient?.admission?.initialAEComplaintDescription || props.patient?.admission?.initialAEComplaintSnomedCode) && !_.isEmpty(dictChiefComplaint)) {
                return chief
                    ? <Chip color="primary" size="small" label={DictParser.chiefComplaint(chief, false)}/>
                    : <Chip color="default" size="small" label={t("NOT TRIAGED")}/>;
            } else {
                return <Chip color="default" size="small" label={t("NOT TRIAGED")}/>;
            }
        }
    };

    const renderReferrals = () => {
        const referrals = [];

        if (!_.isEmpty(dictReferredToService) && !_.isEmpty(props.patient?.studies?.referrals?.ReferralsToOtherServices)) {
            _.forEach(props.patient?.studies?.referrals?.ReferralsToOtherServices, referral => {
                const find = _.find(dictReferredToService, {__id: referral?.ReferredToService_SnomedCt?.__id});
                if (find)
                    referrals.push({
                        id: find.notes,
                        group: find.group,
                        description: find.__description,
                        snomedCode: find.snomedCode,
                        completed: referral.ReferredToServiceAssessmentTime !== ""
                    });
            });
        }

        return referrals;
    };

    const renderPlanIcon = (name) => {
        switch (name){
            case "nursingPlan":
                return (
                    <span className={classes.singleIconFlag}>
                        <AgyleNamedIcon name="nursingPlan" withTooltip/>
                    </span>
                );
            case "medicalPlan":
                return (
                    <span className={classes.singleIconFlag}>
                        <AgyleNamedIcon name="medicalPlan" withTooltip />
                    </span>
                );
            default:
                return;
        }
    }

    const handleTooltipClick = () => {
        setOpen(!open);
    };

    const handleTooltipLeave = () => {
        if (open)
            setOpen(false);
    };

    const handleChangeDemographic = () => {
        if (dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT))) {
            if (props?.patient?.patient?.tags?.includes('temporary')) {
                dispatch(setSnackbar({type: 'warning', message: t('Cannot edit demographic of temporary patient.')}));
            } else {
                dispatch(sendUserAudit(props.patient, AUDIT_TYPES.EDIT_PATIENT_DEMOGRAPHIC));
                dispatch(setDemographicPatient(props.patient));
                history.push('/registry');
            }
        } else {
            dispatch(setSnackbar({type: "warning", message: t("User has no permission for that action.")}));
        }
    }

    const getPatientTime = () => {
        // If patient has arrival mode code for ambulance, then calculate time form ambulance handover
        if (!_.isEmpty(ambulance) && ambulance?.includes(props.patient?.admission?.arrivalModeCode)) {
            return calculateArrivalDate(props.patient);
        } else {
            return moment(props.patient?.admission?.startDate);
        }
    };

    const renderRow = (name) => {
        switch (name) {
            case "total":
                return (
                    <div className={classes.priority}>
                        {`${props?.index + 1}/${props.total}`}
                    </div>
                );
            case "priority":
                return (
                    <div className={classes.priority}>
                        {props.patient.sortData.priority}
                    </div>
                );
            case "link":
                return (
                    <IconButton size="small"
                                onClick={() => props.openPatient(props.patient?.__id)}
                                disableRipple
                                style={{position: "inherit"}}>
                        <ContactPageIcon fontSize="small"/>
                    </IconButton>
                );
            case "linkAdmit":
                return (
                    <IconButton
                        size="small"
                        onClick={handleChangeDemographic}
                        disableRipple
                        style={{position: "inherit"}}
                    >
                        <FolderSharedIcon fontSize="small"/>
                    </IconButton>
                );
            case "name":
                return formatPatientName(props.patient.patient);
            case "age":
                return getPatientAge(props.patient);
            case "gender":
                return props.patient?.patient?.gender ? props.patient?.patient?.gender : "-";
            case "area":
                return (
                    <div style={{fontSize: "small"}}>
                        {props.findAreaBed("area", props.patient)}
                    </div>
                );
            case "bed":
                return (
                    <div style={{fontSize: "small"}}>
                        {props.findAreaBed("bed", props.patient)}
                    </div>
                );
            case "nhsNumb":
                return props.patient?.patient?.hospitalNumber ? props.patient.patient.hospitalNumber : "-";
            case "AEComplaint":
                return (
                    <div className={classes.complaint}>
                        {renderChiefComplaint()}
                    </div>
                );
            case "time":
                return (
                    <div className={classes.timer}>
                        <TimerCountUpSimple
                            time={getPatientTime()}
                            renderWithBG
                            limit={240}
                            upperLimit={-1}
                            limitMessage={t(">8h")}
                        />
                    </div>
                );
            case "obs": {
                const score = props?.patient?.patient?.lastObservation?.totalScore;
                const age = moment.duration(getAdmissionStartDate(props?.patient?.admission).diff(moment(props?.patient?.patient?.dob))).asYears();
                const isAdult = age >= 18;
                let obsColor = 'transparent';

                const themeColors = theme?.palette?.observation;
                if (!isNaN(score)) {
                    if (isAdult) {
                        if (score >= 9) obsColor = themeColors?.red;
                        else if (score < 9 && score >= 7) obsColor = themeColors?.amber;
                        else if (score < 7 && score >= 5) obsColor = themeColors?.yellow;
                        else obsColor = themeColors?.white;
                    } else {
                        if (score >= 13) obsColor = themeColors?.red;
                        else if (score < 13 && score >= 9) obsColor = themeColors?.pink;
                        else if (score < 9 && score >= 5) obsColor = themeColors?.amber;
                        else if (score < 5 && score >= 1) obsColor = themeColors?.yellow;
                        else obsColor = themeColors?.white;
                    }
                }

                return (
                    <span className={clsx(classes.scoreColor)} style={{backgroundColor: obsColor, color: theme?.palette?.observation?.text}}>
                        {props.patient.patient.lastObservation?.totalScore >= 0 ? props.patient.patient.lastObservation.totalScore : '-'}
                    </span>
                );
            }
            case "triage":
                const acuityColor = theme?.palette?.acuityColors["color" + props?.patient?.triage?.discriminator?.priority];
                return (<span className={clsx(classes.triage, classes.scoreColor)} style={{background: acuityColor?.background || '', color: acuityColor?.text}}>
       {props.patient.triage?.discriminator?.priority >= 0 ? props.patient.triage.discriminator.priority : "-"}
    </span>);
            case "clinician":
                return props.patient?.currentClinician ?
                    (<div>
                        {props.patient?.currentClinician?.__description}
                        <div className={classes.clinitianTimestamp}>
                        {props.patient?.currentClinician?.timestamp ? moment(props.patient?.currentClinician?.timestamp).format(config.defaultDateTimeFormat) : ""}
                        </div>
                    </div>)
                    : "-";
            case "seniorReview":
                return props.patient?.seniorReview ?
                    (<div>
                        {props.patient?.seniorReview?.who ? props.patient?.seniorReview?.who.__description : "-"}
                        <div className={classes.clinitianTimestamp}>
                            {props.patient?.seniorReview?.when ? moment(props.patient?.seniorReview?.when).format(config.defaultDateTimeFormat): ""}
                        </div>
                    </div>) : "-";
            case "assessClinician":
                return props.patient?.seen?.who ?
                    (<div>
                        {props.patient?.seen?.who?.__description}
                        <div className={classes.clinitianTimestamp}>
                            {props.patient?.seen?.timestamp ? moment(props.patient?.seen?.timestamp).format(config.defaultDateTimeFormat) : ""}
                        </div>
                    </div>) : "-";
            case "nextObs":
                return (
                    <div style={{fontSize: "small"}}>
                        {props.patient.patient.lastObservation?.nextObsDate ?
                            moment(props.patient.patient.lastObservation.nextObsDate).format(config.defaultDateTimeFormat) : "-"}
                    </div>
                );
            case "escalation":
                return props.patient.patient?.lastObservation
                    ? props.patient.patient?.lastObservation?.metadata?.extraOption1 ? "Yes" : "No"
                    : "-";
            case "referrals": {
                const refs = renderReferrals();

                if (_.isEmpty(refs)) return "-";
                return (
                    <Fragment>
                        {refs.map((ref, index) => (
                            <Tooltip
                                key={index}
                                title={ref.description}
                            >
                                <div
                                    style={{backgroundColor: getReferralsColor(ref)}}
                                    className={clsx(classes.referral, ref.completed && "completed")}
                                >
                                    {ref.id}
                                </div>
                            </Tooltip>
                        ))}
                    </Fragment>
                );
            }
            case "tasks": {
                const flags = props.patient?.flags;
                const {studies} = props?.patient;

                if (_.isEmpty(flags)) return "-";
                return (
                    <div className={classes.flagsContainer}>
                    {flags.map(flag => (<Tooltip title={flag.name} key={flag.name} className={clsx(classes.singleIconFlag, flag.value === 1 && "active")}>
                            <AgyleNamedIcon name={flag.name} withTooltip/>
                        </Tooltip>)
                    )}
                        {studies?.nursingPlanNote && renderPlanIcon("nursingPlan")}
                        {studies?.medicalPlan && renderPlanIcon("medicalPlan")}
                    </div>
                )
            }

            case "flags": {
                const flags = props.patient?.metadata?.patientFlags;
                const flagsToExclude = config?.priorityList?.flagsToExclude || [];
                const activeFlags = flags && flags.filter(singleFlag => !flagsToExclude.includes(singleFlag.name) && singleFlag.checked);

                if (_.isEmpty(activeFlags)) return "-";

                return (
                    <div className={classes.flagsContainer}>
                        {activeFlags && activeFlags.map((flag) => {
                            const obj = findPatientFlagObject(flag?.name);
                            if (!obj) return null;

                            return (
                                <Tooltip title={obj?.title} key={flag?.name}>
                                    <Chip className={classes.flagChip} label={obj?.shortName}/>
                                </Tooltip>
                            );
                        })
                        }
                    </div>
                );
            }

            case "streamingType": {
                if (!props.patient?.metadata?.healthcareServiceId) return '-';
                const findService = _.find(streamingType, sr => sr.healthcareService?.includes(props.patient?.metadata?.healthcareServiceId));
                let tag = 'UNK';
                let color = '#cbcbcb';
                if (findService) {
                    tag = findService?.__code || 'UNK';
                    color = findService?.color || '#cbcbcb';
                }

                return (
                    <div
                        className={classes.streamingType}
                        style={{background: color}}
                        onClick={() => props.openPatient(props.patient?.__id, true)}
                    >
                       {tag}
                    </div>
                );
            }

            case "clinicalConclusion": {
                const { clinicalConclusion } = props.patient;

                if(!clinicalConclusion || !clinicalConclusion.status) return "-";
                return (
                  clinicalConclusion && (
                    <>
                      { clinicalConclusion.by ?  clinicalConclusion.by : clinicalConclusion?.who?.__description || "-"}
                      <div className={classes.clinitianTimestamp}>
                        {moment(clinicalConclusion).format(
                          config.defaultDateTimeFormat
                        )}
                      <Tooltip title={clinicalConclusion.status} style={{transform: "translate(20px, -10px)"}}>
                          {clinicalConclusion.status === "Deceased" ?
                              <AgyleNamedIcon name={"deceased"} withTooltip className={clsx(classes.singleIconFlag, classes.conclusionIcon)} style={{width: '30px', height: '30px'}} /> :
                              <Typography variant="body2" className={clsx(classes.singleIconFlag, classes.smallIconText)} textAlign="center">
                                  {clinicalConclusion?.status?.[0]}
                              </Typography>
                          }
                      </Tooltip>
                      </div>
                    </>
                  )
                );
            }
            case "ambulanceArrivalMode" : {
                const arrivalMode = props.patient?.admission?.arrivalModeDescription;
                function checkAmbulance(str) {
                    const regex = /helicopter | ambulance/i;
                    return regex.test(str);
                }

                if(checkAmbulance(arrivalMode)){
                    return <AgyleNamedIcon name="ambulance"/>;
                }
                return null;
            }
            case "bedReq":
                if (config?.modules?.forms?.bedBooking?.enableBedRequestForm) {
                    return formatBedRequestUHD(props.patient?.studies?.bedRequest);
                }
                return formatBedRequest(props.patient?.csmContactedTime);
            case "covid":
                return renderCovid();
            case "sepsis":
                return renderSepsis();
            case "histComplaint":
                return (
                    <div onClick={handleTooltipClick} onMouseLeave={handleTooltipLeave}>
                        <Tooltip placement="top"
                                 PopperProps={{disablePortal: true,}}
                                 open={open}
                                 title={presentingComplaint}
                        >
                            <div className={classes.presentingComplaintClass} style={{textAlign: "center"}}>
                                {presentingComplaint}
                            </div>
                        </Tooltip>
                    </div>
                );
            case "dependency":
                return formatDependency(props.patient?.triage?.dependency);
            case "nursingPlan":
                return props.patient?.studies?.nursingPlanNote?.notes ? <NursingNotesWithTooltip note={props.patient?.studies?.nursingPlanNote?.notes}/> : "-";
            case "medicalPlan":
                return props.patient?.studies?.medicalPlan?.notes ? <NursingNotesWithTooltip note={props.patient?.studies?.medicalPlan?.notes}/> : "-";
            case "treatmentPlan":
                return props.patient?.studies?.treatmentPlan?.notes ? <NursingNotesWithTooltip note={props.patient?.studies?.treatmentPlan?.notes}/> : "-";
            case "diagnosis":
                return props.patient?.diagnosis?.primary ?  <NursingNotesWithTooltip note={props.patient?.diagnosis?.primary} fieldName="diagnosis" /> : "-";
            case "treatment":
                return props.patient?.triage?.treatmentAndManagementPlan ? <NursingNotesWithTooltip note={props.patient?.triage?.treatmentAndManagementPlan} fieldName="treatment" /> : "-";
            case "investigations":
                return props.patient?.triage?.investigations ? <NursingNotesWithTooltip note={props.patient?.triage?.investigations} fieldName="investigations" /> : "-";
            case "safeguardingLevel":
                return renderSafeguardingLevel()
                default:
                return "***";

        }
    };

    return (
        <div className={classes.main}>
            {props.patient && props.patient.sortData && (
                <Grid container direction="row" columns={props.gridWidth()}>
                    {props.columns.map((col, index) => (
                        <Grid item xs={col.gridWidth} key={`${index}row_${col.name}`}>
                            {renderRow(col.name)}
                        </Grid>
                    ))}
                </Grid>
            )}
        </div>
    );
}

export default PatientInList;
